import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { JsonObjectFactoryService } from '../../../global/services/json-object-factory.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1118-attach-schd',
    templateUrl: './f1118-attach-schd.component.html',
    styleUrls: ['./f1118-attach-schd.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1118AttachSchdComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  private isInitialized = false;
  private isLoading = false;

  minimise = false;
  loadingData = true;

  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  gridData: any = {};

  generalInfo: {
    basketName?: string;
    schdName?: string;
    country?: string;
    ein?: string;
    colNumber?: string;
  } = {};

  resultJson: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = g != null && typeof g === 'object' ? g : {};
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.GENERAL_CONFIG?.base_url || !this.gridData || Object.keys(this.gridData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      if (this.gridData && this.gridData.colData) {
        const rowData = this.gridData.rowData;
        this.generalInfo.basketName = this.gtwWorkspace.workspaceFactory?.activity?.label || '';
        this.generalInfo.schdName = this.gtwWorkspace.workspaceFactory?.activeScreen?.label || '';
        this.generalInfo.country = rowData?.ELEMENT_NAME || '';
        this.generalInfo.ein = rowData?.ELEMENT_NAME_1 || '';
      }

      this.getAltGIESplitupAmt();
    } catch (e) {
      console.error('Error in component init:', e);
      this.loadingData = false;
      this.cdr.detectChanges();
    }
  }

  getAltGIESplitupAmt(): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.loadingData = true;
    this.cdr.detectChanges();

    if (this.gridData && this.gridData.colData) {
      const rowData = this.gridData.rowData;
      const colNumber = this.gridData.colData.map ? this.gridData.colData.map.split('_')[0] : '';
      this.generalInfo.colNumber = colNumber;

      const params = {
        action_code: '1zrq5v',
        basket_key: rowData?.BASKET_KEY || '',
        schd_name: 'F1118_A',
        line_key: rowData?.LINE_KEY || '',
        col_number: colNumber,
        ein: rowData?.ELEMENT_NAME_1 || '',
      };

      this.jsonObjectFactory.getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.resultJson = [];
          this.loadingData = false;
          this.isLoading = false;

          if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
            this.gtwSnackbar.openSnackBar(data.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.resultJson = data.jsonObject || [];
            this.processAmountFormatting();
          }

          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading data:', error);
          this.loadingData = false;
          this.isLoading = false;
          this.gtwSnackbar.openSnackBar('Error loading attachment details. Please try again.', 'gtw-snackbar--red', true, 4);
          this.cdr.detectChanges();
        }
      );
    } else {
      this.loadingData = false;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private processAmountFormatting(): void {
    if (this.resultJson && this.resultJson.length > 0) {
      this.resultJson.forEach((item: any) => {
        if (item.AMOUNT) {
          const amtWithoutComma = parseInt(item.AMOUNT.replace(/,/g, ''), 10);
          if (amtWithoutComma < 0) {
            item.AMOUNT_FORMATTED = item.AMOUNT.replace('-', '(') + ')';
          } else {
            item.AMOUNT_FORMATTED = item.AMOUNT;
          }
        } else {
          item.AMOUNT_FORMATTED = item.AMOUNT || '';
        }
      });
    }
  }

  isNegativeAmount(item: any): boolean {
    return item.AMOUNT_FORMATTED !== item.AMOUNT;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
